import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, CalendarDays, Info } from 'lucide-react';
import {
  Button,
  ConfirmDialog,
  EmptyState,
  PageHeader,
  Select,
  Spinner,
  useToast,
  type SelectOption,
} from '../../ui';
import { useDailyUpdates } from '../../features/dailyUpdates/useDailyUpdates';
import {
  collectEngineers,
  filterDailyUpdates,
  groupByDayEngineer,
} from '../../features/dailyUpdates/dailyUpdateModel';
import type {
  DailyUpdateFilter,
  DailyUpdateSummary,
} from '../../features/dailyUpdates/types';
import { draftFromDailyUpdate } from '../../features/blockers/blockerModel';
import type { BlockerDraft } from '../../features/blockers/types';
import { DailyUpdateComposer } from './DailyUpdateComposer';
import { DailyUpdateFeed } from './DailyUpdateFeed';
import { BlockerModal } from '../Blockers/BlockerModal';

const EMPTY_FILTER: DailyUpdateFilter = { engineer: '' };

/**
 * Daily Updates — a lightweight standup feed. The composer at the top makes a
 * useful update a ~1-minute task; the feed below groups updates by day and
 * engineer for team awareness. When an update notes a blocker, a one-click
 * action converts it into a structured Blocker (pre-filled, human-verified)
 * that lands on the Blockers board.
 */
export function DailyUpdatesPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const {
    updates,
    tasks,
    workstreams,
    goals,
    currentAlias,
    storyTitles,
    workstreamTitles,
    goalTitles,
    loading,
    error,
    create,
    remove,
    createBlocker,
  } = useDailyUpdates();

  const [filter, setFilter] = useState<DailyUpdateFilter>(EMPTY_FILTER);
  const [blockerSeed, setBlockerSeed] = useState<BlockerDraft | null>(null);
  const [pendingDelete, setPendingDelete] =
    useState<DailyUpdateSummary | null>(null);
  const [deleting, setDeleting] = useState(false);

  const engineerOptions = useMemo<SelectOption[]>(
    () => collectEngineers(updates).map((e) => ({ value: e, label: e })),
    [updates],
  );

  const filtered = useMemo(
    () => filterDailyUpdates(updates, filter),
    [updates, filter],
  );
  const dayGroups = useMemo(() => groupByDayEngineer(filtered), [filtered]);
  const filtersActive = !!filter.engineer;

  const onPost = async (draft: Parameters<typeof create>[0]) => {
    const saved = await create(draft);
    toast({ tone: 'success', title: 'Update posted' });
    return saved;
  };

  const onDelete = (update: DailyUpdateSummary) => setPendingDelete(update);

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await remove(pendingDelete.id);
      toast({ tone: 'success', title: 'Update removed' });
      setPendingDelete(null);
    } catch (e) {
      toast({
        tone: 'error',
        title: 'Could not remove update',
        description: e instanceof Error ? e.message : 'Delete failed.',
      });
    } finally {
      setDeleting(false);
    }
  };

  const onConvertBlocker = (update: DailyUpdateSummary) => {
    setBlockerSeed(draftFromDailyUpdate(update));
  };

  const onCreateBlocker = async (draft: BlockerDraft) => {
    const saved = await createBlocker(draft);
    toast({
      tone: 'success',
      title: 'Blocker created',
      description: 'Tracked on the Blockers board.',
    });
    setBlockerSeed(null);
    navigate('/blockers');
    return saved;
  };

  return (
    <div>
      <PageHeader
        icon={<CalendarDays className="h-5 w-5" />}
        title="Daily Updates"
        description="A ~1-minute standup: what you're on, what you learned, what's blocking you."
      />

      {/* Deferred auto-collection notice (same posture as the Change Feed). */}
      <div className="mb-5 flex items-start gap-2 rounded-xl border border-surface-border bg-surface-1/60 px-4 py-3 text-sm text-zinc-400">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-zinc-500" aria-hidden="true" />
        <p>
          Updates are entered manually today. Auto-collection from Jira, repos,
          and deploys is planned but not available yet — it will pre-fill a
          candidate update for you to confirm, leaving the context to you.
        </p>
      </div>

      <DailyUpdateComposer
        defaultEngineer={currentAlias}
        tasks={tasks}
        workstreams={workstreams}
        goals={goals}
        onPost={onPost}
      />

      {/* Filter. */}
      <div className="mb-5 flex flex-wrap items-end gap-3">
        <div className="w-48">
          <Select
            label="Engineer"
            placeholder="All engineers"
            value={filter.engineer}
            options={engineerOptions}
            onChange={(e) => setFilter({ engineer: e.target.value })}
          />
        </div>
        {filtersActive && (
          <Button variant="ghost" size="sm" onClick={() => setFilter(EMPTY_FILTER)}>
            Clear filter
          </Button>
        )}
        <span className="ml-auto self-center text-xs text-zinc-500">
          {filtered.length} of {updates.length}{' '}
          {updates.length === 1 ? 'update' : 'updates'}
        </span>
      </div>

      {/* Content. */}
      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Spinner label="Loading daily updates" />
        </div>
      ) : error ? (
        <EmptyState
          icon={<AlertTriangle className="h-6 w-6" />}
          title="Couldn't load daily updates"
          description={error}
        />
      ) : updates.length === 0 ? (
        <EmptyState
          icon={<CalendarDays className="h-6 w-6" />}
          title="No updates yet"
          description="Post the first update above — it takes about a minute and keeps the team in sync."
        />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={<CalendarDays className="h-6 w-6" />}
          title="No updates match this filter"
          description="Try clearing the filter to see the whole feed."
        />
      ) : (
        <DailyUpdateFeed
          dayGroups={dayGroups}
          storyTitles={storyTitles}
          workstreamTitles={workstreamTitles}
          goalTitles={goalTitles}
          onOpenWorkstream={(id) => navigate(`/workstreams/${encodeURIComponent(id)}`)}
          onOpenGoal={(id) => navigate(`/goals/${encodeURIComponent(id)}`)}
          onConvertBlocker={onConvertBlocker}
          onDelete={onDelete}
        />
      )}

      {/* Convert-to-blocker modal (seeded from the daily update). */}
      <BlockerModal
        open={blockerSeed !== null}
        onClose={() => setBlockerSeed(null)}
        defaultEngineer={currentAlias}
        seedDraft={blockerSeed ?? undefined}
        goals={goals}
        workstreams={workstreams}
        tasks={tasks}
        onCreate={onCreateBlocker}
        title="Convert to structured blocker"
      />

      <ConfirmDialog
        open={!!pendingDelete}
        title="Remove update?"
        confirmLabel="Remove update"
        loading={deleting}
        onConfirm={() => void confirmDelete()}
        onClose={() => setPendingDelete(null)}
        message="Remove this daily update from the feed? This can't be undone."
      />
    </div>
  );
}

export default DailyUpdatesPage;
